import React from 'react';
import { FileText, Youtube, Download, Tag, MapPin, Calendar, Clock } from 'lucide-react';
import { Subject } from '../../types/subject';
import { downloadPdf } from '../../services/subjectService';
import toast from 'react-hot-toast';

interface SubjectCardProps {
  subject: Subject;
}

const chapterLabels: Record<string, string> = {
  'croissance': 'Croissance économique',
  'commerce': 'Commerce international',
  'chomage': 'Chômage',
  'crise': 'Crises financières',
  'europe': 'Europe',
  'structure': 'Structure sociale',
  'ecole': 'École',
  'mobilite': 'Mobilité sociale',
  'travail': 'Travail-Emploi',
  'engagement': 'Engagement politique',
  'justice': 'Justice sociale',
  'environnement': 'Environnement'
};

const locationLabels: Record<string, string> = {
  'metropole': 'France métropolitaine',
  'asie': 'Asie',
  'amerique-nord': 'Amérique du Nord',
  'amerique-sud': 'Amérique du Sud',
  'polynesie': 'Polynésie',
  'etrangers': 'Centres étrangers',
  'reunion': 'La Réunion',
  'liban': 'Liban',
  'caledonie': 'Nouvelle Calédonie'
};

const SubjectCard: React.FC<SubjectCardProps> = ({ subject }) => {
  const handleDownload = async () => {
    try {
      // Utiliser la nouvelle fonction de téléchargement compatible avec Safari
      await downloadPdf(subject.pdfUrl, `sujet-${subject.type}-${subject.chapter}-${subject.year}.pdf`);
      toast.success('Téléchargement lancé !');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'dissertation':
        return 'Dissertation';
      case 'ec1':
        return 'EC1 - Mobilisation';
      case 'ec2':
        return 'EC2 - Analyse';
      case 'ec3':
        return 'EC3 - Raisonnement';
      default:
        return type;
    }
  };

  const getSessionLabel = (session: string) => {
    switch (session) {
      case 'normale':
        return 'Session normale';
      case 'remplacement':
        return 'Session de remplacement';
      case 'candidat-libre':
        return 'Candidat libre';
      default:
        return session;
    }
  };

  // Couleurs plus douces pour chaque type d'épreuve
  const getTypeColors = (type: string) => {
    switch (type) {
      case 'dissertation':
        return {
          bg: 'bg-purple-50 border-purple-200 hover:bg-white',
          badge: 'bg-purple-100 text-purple-700',
          icon: 'text-purple-400',
          button: 'bg-purple-200 text-purple-700 hover:bg-purple-300',
          outline: 'text-purple-600 border-purple-200 hover:bg-purple-50'
        };
      case 'ec1':
        return {
          bg: 'bg-blue-50 border-blue-200 hover:bg-white',
          badge: 'bg-blue-100 text-blue-700',
          icon: 'text-blue-400',
          button: 'bg-blue-200 text-blue-700 hover:bg-blue-300',
          outline: 'text-blue-600 border-blue-200 hover:bg-blue-50'
        };
      case 'ec2':
        return {
          bg: 'bg-emerald-50 border-emerald-200 hover:bg-white',
          badge: 'bg-emerald-100 text-emerald-700',
          icon: 'text-emerald-400',
          button: 'bg-emerald-200 text-emerald-700 hover:bg-emerald-300',
          outline: 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'
        };
      case 'ec3':
        return {
          bg: 'bg-amber-50 border-amber-200 hover:bg-white',
          badge: 'bg-amber-100 text-amber-700',
          icon: 'text-amber-400',
          button: 'bg-amber-200 text-amber-700 hover:bg-amber-300',
          outline: 'text-amber-600 border-amber-200 hover:bg-amber-50'
        };
      default:
        return {
          bg: 'bg-white border-gray-200 hover:bg-gray-50',
          badge: 'bg-gray-100 text-gray-700',
          icon: 'text-gray-400',
          button: 'bg-gray-200 text-gray-700 hover:bg-gray-300',
          outline: 'text-gray-600 border-gray-200 hover:bg-gray-50'
        };
    }
  };

  const colors = getTypeColors(subject.type);

  return (
    <div className={`rounded-xl shadow-md transition-all duration-200 border-2 ${colors.bg}`}>
      {/* En-tête avec type, année et localisation */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${colors.badge} mb-2 sm:mb-0 whitespace-nowrap`}>
            {getTypeLabel(subject.type)}
          </span>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 text-sm text-gray-600 w-full">
            <span className="inline-flex items-center">
              <Calendar size={16} className={`mr-1 ${colors.icon}`} />
              {subject.year}
            </span>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center">
                <MapPin size={16} className={`mr-1 ${colors.icon}`} />
                {locationLabels[subject.location] || subject.location}
              </span>
              {subject.jour && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                  <Clock size={14} className="mr-1" />
                  {subject.jour === 'j1' ? 'Jour 1' : 'Jour 2'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-6 py-4">
        <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-3">
          {subject.title}
        </h3>
        
        {/* Questions pour EC2 */}
        {subject.type === 'ec2' && (subject.q1 || subject.q2) && (
          <div className="mt-3 space-y-2 bg-white p-4 rounded-lg border-2 border-emerald-300">
            {subject.q1 && (
              <p className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Q1 :</span> {subject.q1}
              </p>
            )}
            {subject.q2 && (
              <p className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Q2 :</span> {subject.q2}
              </p>
            )}
          </div>
        )}
        
        <div className="flex items-center gap-4 text-sm text-gray-600 mt-4">
          <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200">
            <Tag size={16} className={`mr-1 ${colors.icon}`} />
            <span className="text-center">{chapterLabels[subject.chapter] || subject.chapter}</span>
          </span>
          <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200">
            <FileText size={16} className={`mr-1 ${colors.icon}`} />
            <span className="text-center">{getSessionLabel(subject.session)}</span>
          </span>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="px-6 py-4 bg-gray-50/50 rounded-b-xl border-t border-gray-100">
        <div className="flex gap-2">
          <button
            onClick={handleDownload}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${colors.button}`}
          >
            <Download size={16} />
            <span className="text-center w-full">Télécharger le PDF</span>
          </button>
          <a
            href={subject.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors ${colors.outline}`}
          >
            <Youtube size={16} />
            <span className="text-center w-full">Voir la correction</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubjectCard;